import { AMOUNT_TYPE } from '@/utils/enum';

/**
 * Helpers cho "nhiều chương trình chiết khấu trên một dòng".
 * Bám tài liệu FE_DISCOUNT_PROGRAM_MULTI_STACK.md:
 *  - extraDiscountProgramIds: CSV "2,3" (program phụ, không chứa program chính)
 *  - stackableProgramIds (admin): whitelist pairwise (mục 4)
 *  - discountProgramAmount: tổng tiền CK parallel + cap giá dòng, roundDown 1 decimal (mục 5)
 */

/** Chuẩn hoá 1 giá trị (CSV string | array | number) -> mảng số nguyên dương, giữ thứ tự xuất hiện, dedupe. */
export function parseProgramIdList(value) {
  if (value == null || value === '') return [];
  let parts = [];
  if (Array.isArray(value)) {
    parts = value;
  } else if (typeof value === 'string') {
    parts = value.split(/[,;\s]+/);
  } else {
    parts = [value];
  }
  const out = [];
  const seen = new Set();
  parts.forEach((raw) => {
    const n = Number(typeof raw === 'string' ? raw.trim() : raw);
    if (Number.isInteger(n) && n > 0 && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  });
  return out;
}

/** Đọc extraDiscountProgramIds từ payload/API -> mảng số. */
export function parseExtraDiscountProgramIds(value) {
  return parseProgramIdList(value);
}

/**
 * Serialize danh sách program phụ thành CSV "2,3" (sort tăng dần, dedupe),
 * loại bỏ id chính nếu lỡ lọt vào. Rỗng -> null (giữ hành vi legacy).
 */
export function serializeExtraDiscountProgramIds(extraIds, mainId = null) {
  const main = Number(mainId);
  const ids = parseProgramIdList(extraIds)
    .filter((id) => !(Number.isInteger(main) && main > 0 && id === main))
    .sort((a, b) => a - b);
  return ids.length ? ids.join(',') : null;
}

/** Đọc stackableProgramIds (admin) từ API -> mảng số. */
export function parseStackableProgramIds(value) {
  return parseProgramIdList(value);
}

/** Serialize stackableProgramIds (admin) -> CSV sort tăng dần, dedupe. Rỗng -> null. */
export function serializeStackableProgramIds(ids) {
  const list = parseProgramIdList(ids).sort((a, b) => a - b);
  return list.length ? list.join(',') : null;
}

/**
 * Gom mọi program id trên dòng (cho voucher validate: stackedDiscountProgramIds).
 * Trả [main, ...extras] (loại extras trùng main). Nếu không có main -> chỉ extras.
 */
export function collectProgramIds(line) {
  if (!line) return [];
  const main = Number(line.discountProgramId);
  const extras = parseExtraDiscountProgramIds(line.extraDiscountProgramIds);
  if (!Number.isInteger(main) || main <= 0) {
    return extras;
  }
  return [main, ...extras.filter((id) => id !== main)];
}

/**
 * Rule A — pairwise whitelist (mục 4.3).
 * stackMapById: { [programId]: number[] } - stackableProgramIds của từng program.
 * Mọi cặp (A, B) phải thoả: B ∈ stack(A) HOẶC A ∈ stack(B).
 */
export function isPairOfProgramsStackable(idA, idB, stackMapById) {
  const a = Number(idA);
  const b = Number(idB);
  if (a === b) return true;
  const stackA = parseProgramIdList((stackMapById || {})[a]);
  const stackB = parseProgramIdList((stackMapById || {})[b]);
  return stackA.includes(b) || stackB.includes(a);
}

/** Kiểm tra cả tập program id có pairwise hợp lệ không. */
export function isSelectionPairwiseStackable(programIds, stackMapById) {
  const ids = parseProgramIdList(programIds);
  for (let i = 0; i < ids.length; i += 1) {
    for (let j = i + 1; j < ids.length; j += 1) {
      if (!isPairOfProgramsStackable(ids[i], ids[j], stackMapById)) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Có thể thêm candidateId vào tập đang chọn mà vẫn pairwise hợp lệ?
 * Dùng để enable/disable checkbox program phụ trên UI (mục 4.3 - gợi ý UI).
 */
export function canAddProgramToSelection(selectedIds, candidateId, stackMapById) {
  const candidate = Number(candidateId);
  if (!Number.isInteger(candidate) || candidate <= 0) return false;
  const selected = parseProgramIdList(selectedIds);
  return selected.every((id) =>
    isPairOfProgramsStackable(id, candidate, stackMapById),
  );
}

/**
 * roundDown tới `decimals` chữ số thập phân (khớp backend roundDown(..., 1)).
 */
export function roundDownDecimal(value, decimals = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const factor = Math.pow(10, decimals);
  return Math.floor(n * factor) / factor;
}

/**
 * Tiền CK của 1 program trên lineBase (mục 5.2), không trừ dần (parallel).
 * program: { discountType, discountAmount, maxValue }
 */
export function programDiscountMoney(program, lineBase) {
  const base = Number(lineBase) || 0;
  if (base <= 0 || !program) return 0;
  const type = Number(program.discountType ?? AMOUNT_TYPE.MONEY);
  const amount = Number(program.discountAmount ?? 0);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  if (type === AMOUNT_TYPE.PERCENT) {
    const raw = (base * amount) / 100;
    const maxValue = Number(program.maxValue ?? 0);
    const money = maxValue > 0 ? Math.min(raw, maxValue) : raw;
    return Math.min(money, base);
  }
  // discountType = MONEY: discountAmount là mức giảm trên cả dòng
  return Math.min(amount, base);
}

/**
 * Tổng tiền CK của nhiều program trên cùng lineBase (parallel + cap), roundDown 1 decimal (mục 5.2).
 * programs: mảng { discountType, discountAmount, maxValue }
 */
export function computeStackedProgramAmount(programs, lineBase) {
  const base = Number(lineBase) || 0;
  if (base <= 0 || !Array.isArray(programs) || !programs.length) return 0;
  const sum = programs.reduce(
    (acc, program) => acc + programDiscountMoney(program, base),
    0,
  );
  const capped = Math.min(sum, base);
  return roundDownDecimal(capped, 1);
}
